import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { WACHTWOORD_MIN_LENGTE, keurWachtwoord } from '@fleet/shared';
import { Button, Input, Logo } from '../../design-system';
import { ApiError } from '../../lib/apiClient';
import { authApi } from './auth.api';
import { useAuth } from './useAuth';
import { WachtwoordMeter } from './WachtwoordMeter';
import styles from './LoginPage.module.css';

/**
 * Het scherm achter de uitnodigingslink uit de mail. Publiek bereikbaar, want
 * wie hier komt heeft nog geen account. Opzet gelijk aan het inlogscherm, zodat
 * het niet aanvoelt als een andere applicatie.
 */
export function AccountAanmakenPage() {
  const { token = '' } = useParams();
  const { maakAccount } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState<string | null>(null);
  const [linkFout, setLinkFout] = useState('');
  const [laden, setLaden] = useState(true);

  const [naam, setNaam] = useState('');
  const [wachtwoord, setWachtwoord] = useState('');
  const [herhaling, setHerhaling] = useState('');
  const [fouten, setFouten] = useState<string[]>([]);
  const [bezig, setBezig] = useState(false);

  // De link eerst laten keuren door de server: verlopen of al gebruikt moet je
  // meteen zien, niet pas nadat je een wachtwoord hebt zitten bedenken.
  useEffect(() => {
    let actueel = true;
    authApi
      .checkUitnodiging(token)
      .then((r) => actueel && setEmail(r.email))
      .catch(
        (err) =>
          actueel &&
          setLinkFout(
            err instanceof ApiError ? err.message : 'Deze link kon niet worden gecontroleerd.',
          ),
      )
      .finally(() => actueel && setLaden(false));
    return () => {
      actueel = false;
    };
  }, [token]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFouten([]);

    // Dezelfde regels als de server, zodat je niet hoeft te wachten op een
    // rondje netwerk voor iets dat hier al te zien is.
    const lokaal = keurWachtwoord(wachtwoord, { email: email ?? '', naam });
    if (wachtwoord !== herhaling) lokaal.push('De twee wachtwoorden zijn niet gelijk.');
    if (lokaal.length) return setFouten(lokaal);

    setBezig(true);
    try {
      await maakAccount(token, { naam, wachtwoord });
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        // De server stuurt zijn bezwaren als lijst mee; die zijn preciezer dan
        // de samengevatte melding.
        const details = (err.details as { fouten?: string[] } | undefined)?.fouten;
        setFouten(details?.length ? details : [err.message]);
      } else {
        setFouten(['Er ging iets mis. Probeer het opnieuw.']);
      }
    } finally {
      setBezig(false);
    }
  }

  return (
    <div className={styles.screen}>
      <div className={styles.card}>
        <span className={styles.logo}>
          <Logo height={36} />
        </span>

        {laden ? (
          <p className={styles.sub}>Even kijken of deze link nog geldig is…</p>
        ) : linkFout ? (
          <div>
            <div className={styles.eyebrow}>Wagenpark</div>
            <div className={styles.rule} />
            <h1 className={styles.title}>Deze link werkt niet meer.</h1>
            <p className={styles.sub}>{linkFout}</p>
            <p className={styles.sub}>
              Vraag degene die je heeft uitgenodigd om een nieuwe uitnodiging. Een link is één dag
              geldig en kan één keer gebruikt worden.
            </p>
          </div>
        ) : (
          <>
            <div>
              <div className={styles.eyebrow}>Wagenpark</div>
              <div className={styles.rule} />
              <h1 className={styles.title}>Welkom aan boord.</h1>
              <p className={styles.sub}>
                Je maakt een account voor <strong>{email}</strong>. Kies een wachtwoord, daarna ben
                je meteen ingelogd.
              </p>
            </div>

            <form className={styles.form} onSubmit={onSubmit}>
              <Input
                label="Je naam"
                value={naam}
                onChange={(e) => setNaam(e.target.value)}
                placeholder="Voor- en achternaam"
                autoComplete="name"
                required
              />
              <Input
                label="Wachtwoord"
                type="password"
                value={wachtwoord}
                onChange={(e) => setWachtwoord(e.target.value)}
                placeholder="••••••••••••••••"
                autoComplete="new-password"
                hint={`Minimaal ${WACHTWOORD_MIN_LENGTE} tekens.`}
                required
              />
              <WachtwoordMeter wachtwoord={wachtwoord} eigenWoorden={[naam, email ?? '']} />
              <Input
                label="Wachtwoord nog een keer"
                type="password"
                value={herhaling}
                onChange={(e) => setHerhaling(e.target.value)}
                placeholder="••••••••••••••••"
                autoComplete="new-password"
                required
              />
              {fouten.length > 0 && (
                <div className={styles.error}>
                  {fouten.map((f) => (
                    <div key={f}>{f}</div>
                  ))}
                </div>
              )}
              <Button variant="primary" block type="submit" disabled={bezig}>
                {bezig ? 'Bezig…' : 'Account aanmaken'}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
